/**
 * Web3 Provider
 * 
 * Provides wagmi and Reown AppKit context to the application.
 * Wraps children with necessary providers for wallet connection.
 */

import { memo, type ReactNode } from "react";
import { WagmiProvider } from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { wagmiConfig } from "@/lib/web3";

// Create a client for React Query (required by wagmi)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't refetch on window focus for wallet data
      refetchOnWindowFocus: false,
      // Stale time for blockchain data
      staleTime: 1000 * 60, // 1 minute
    },
  },
});

interface Web3ProviderProps {
  children: ReactNode;
}

/**
 * Web3Provider component
 * 
 * Provides wallet connection functionality to the app.
 * Should wrap the main App component or the parts that need web3 access.
 * 
 * @example
 * ```tsx
 * <Web3Provider>
 *   <App />
 * </Web3Provider>
 * ```
 */
export const Web3Provider = memo(function Web3Provider({ children }: Web3ProviderProps) {
  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        {children}
      </QueryClientProvider>
    </WagmiProvider>
  );
});

Web3Provider.displayName = "Web3Provider";

export default Web3Provider;
